import React from 'react';
import { Control } from 'react-hook-form';
import { IProjectUser } from '../../../api-client/autogenerated';
import { getUserCompanyName } from '../../../scripts/utils';
import FormSelect from './FormSelect';

/**
 * A role field that names people on the project rather than taking free text: the options are the
 * project's users, shown as name over company the way the Construction Setup administrator
 * dropdowns are.
 *
 * Several of them show as removable pills inside the field, each naming its person and company.
 *
 * The stored value is the rendered "Name (Company)" label, not a user id. These forms are a record
 * of who was named at the time it was filled in, so the text has to keep reading correctly after a
 * user leaves the project -- and the public, unauthenticated form view has no project users to
 * resolve an id against. A value that is not among the options (typed before this field became a
 * dropdown, or naming someone since removed) is offered as its own option so it survives a save
 * untouched.
 */
export default function FormUserSelect({
  name,
  control,
  projectUsers,
  value,
  multiple,
  disabled,
}: {
  name: string;
  control: Control<any>;
  projectUsers: IProjectUser[];
  /** The field's current value, so a stored name outside the options can still be offered. */
  value?: string | string[] | null;
  multiple?: boolean;
  disabled?: boolean;
}) {
  const options = React.useMemo(() => {
    const userOptions = projectUsers.map((projectUser) => {
      const company = getUserCompanyName(projectUser.user, 'No company found');
      return {
        value: `${projectUser.user?.name} (${company})`,
        label: (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 500 }}>{projectUser.user?.name}</span>
            <span>{company}</span>
          </div>
        ),
      };
    });

    const selected = (multiple ? value : value ? [value] : []) as string[];
    const unknown = (selected || [])
      .filter((entry) => !!entry && userOptions.every((option) => option.value !== entry))
      .map((entry) => ({ value: entry, label: <span>{entry}</span> }));

    return [...unknown, ...userOptions];
  }, [projectUsers, value, multiple]);

  return (
    <FormSelect
      name={name}
      control={control}
      options={options}
      multiple={multiple}
      disabled={disabled}
      displayEmpty
      // Multi-select shows its picks as removable pills, and the field grows to hold them.
      chips={multiple}
      // Single selection would otherwise render the option's two-line name-over-company block in
      // the closed field, which does not fit the form's one-line rows.
      renderValue={multiple ? undefined : (selected) => selected as string}
    />
  );
}
